import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { InputPEDetailInfo } from '../../shared/component/headless/input/InputBody';
import { DeviceType, DisplayAlignment, DisplaySize, ImageStyle, PEInfoType, TextAlignment } from '../../shared/enum/EnumRepository';
import { WorkDetailData } from '../../shared/dto/EntityRepository';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { ImgContentInfoWrapper, isCenterVisible } from './WorkInfo';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { DETAIL_DESCRIPTION_MAX_LENGTH } from '../../shared/constants/limits';

export interface DetailProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  imageStyle: ImageStyle;
  infoType: PEInfoType;
  data: WorkDetailData;
}

const DetailInfo: React.FC<DetailProps> = ({ peId, alignment, displaySize, imageStyle, infoType, data }) => {
  const handleChange = useProjectElementListStore((s) => s.updateDetail);
  const { deviceType } = useDeviceStore();

  const defaultAlignment = alignment || DisplayAlignment.CENTER;
  const defaultDisplaySize = displaySize || DisplaySize.BIG;
  const defaultImageStyle = imageStyle || ImageStyle.LANDSCAPE;
  const centerVisible = useMemo(
    () => isCenterVisible(alignment, displaySize, imageStyle, infoType),
    [alignment, displaySize, imageStyle, infoType]
  );

  if (infoType === PEInfoType.NONE) return null;

  return (
    <ImgContentInfoWrapper $centerVisible={centerVisible}>
      <TitleInfoWrpper
        $displayAlignment={defaultAlignment}
        $displaySize={defaultDisplaySize}
        $imageStyle={defaultImageStyle}
        $isMobile={deviceType === DeviceType.MOBILE}>
        <MoleculeInputDiv
          value={data.description}
          placeholder={"Describe this detail — provide context on its material, texture, or technique."}
          handleChange={(e) => handleChange(peId, { description: e.target.value })}
          inputStyle={InputPEDetailInfo}
          StyledDiv={Description}
          alignment={TextAlignment.CENTER}
          maxLength={DETAIL_DESCRIPTION_MAX_LENGTH}
        />
      </TitleInfoWrpper>
    </ImgContentInfoWrapper>
  );
};

const TitleInfoWrpper = styled.div<{
  $displayAlignment: DisplayAlignment,
  $displaySize: DisplaySize,
  $imageStyle: ImageStyle,
  $isMobile: boolean
}>`
  width: ${({ $isMobile }) => $isMobile ? '80%' : '50%'};
  height: ${({ $displayAlignment, $displaySize, $imageStyle }) =>
    $displayAlignment === DisplayAlignment.CENTER ||
      ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE)
      ? 'fit-content' : '0vh'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  transition: height 0.6s ease;
`

const Description = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-align: center;
  ${({ theme }) => theme.typography.Body_04};
`;

export default DetailInfo;
